// People Port backend integration — matches com.navi.springboot.adapter.inbound.rest.PeoplePortController
// Base path on the backend is /api/people; createApiClient()'s baseURL already ends in /api,
// so every path below is prefixed with /people only.
import { createApiClient } from '../../api/apiClient';

const BASE = '/people';

// ─── Shell & Config ────────────────────────────────────────────────────────────
export const fetchPeopleConfig = async (): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/config`);
  return res.data;
};

// ─── My Team ───────────────────────────────────────────────────────────────────
export const fetchMyTeam = async (locationName: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/my-team`, { params: { locationName } });
  return res.data;
};

export const fetchMyTeamSummary = async (
  locationName: string,
  clientTimestamp?: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/my-team/summary`, {
    params: { locationName, ...(clientTimestamp ? { clientTimestamp } : {}) },
  });
  return res.data;
};

export const fetchEmployeeCadence = async (gpid: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${gpid}/cadence`);
  return res.data;
};

// Shared GPID directory lookup (same /common endpoint the config module's Add Employee
// form uses) — prefills first/last name + role on the Add Employee form once GPID hits
// 8 digits. Returns null (not undefined) when the backend has no match for that GPID.
export interface GpidLookupResult {
  first_name?: string;
  last_name?: string;
  job_description?: string;
  manager?: string;
}

export const lookupEmployeeByGpid = async (gpid: string): Promise<GpidLookupResult | null> => {
  const api = createApiClient();
  try {
    const res = await api.get(`/common/employees/gpid/${gpid}`);
    return res.data?.data ?? res.data ?? null;
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 404) return null;
    throw err;
  }
};

export interface AddEmployeeRequest {
  gpid: string;
  firstName: string;
  lastName: string;
  role: string;
  shift?: string;
  department?: string;
  organization?: string;
  manager?: string;
  locationName?: string;
  locationId?: number | string;
  hireDate?: string;
  status?: string;
  peerCoach?: string;
  preferredLanguage?: string;
  newHire?: boolean;
}

export const addEmployee = async (request: AddEmployeeRequest): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/employees`, request);
  return res.data;
};

export interface UpdateEmployeeRequest {
  firstName?: string;
  lastName?: string;
  role?: string;
  shift?: string;
  department?: string;
  organization?: string;
  locationName?: string;
  locationId?: number | string;
  manager?: string;
  status?: string;
  peerCoach?: string;
  preferredLanguage?: string;
  newHire?: boolean;
  hireDate?: string;
}

export const updateEmployee = async (
  gpid: string,
  request: UpdateEmployeeRequest
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.put(`${BASE}/employees/${gpid}`, request);
  return res.data;
};

// ─── Attendance ────────────────────────────────────────────────────────────────
export const fetchAttendance = async (gpid: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${gpid}/attendance`);
  return res.data;
};

export const addAttendancePoint = async (
  gpid: string,
  request: { eventDate: string; points: number; reason: string }
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/employees/${gpid}/attendance`, request);
  return res.data;
};

export const voidAttendancePoint = async (
  gpid: string,
  pointId: number | string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.delete(`${BASE}/employees/${gpid}/attendance/${pointId}`);
  return res.data;
};

// ─── Time-Off ──────────────────────────────────────────────────────────────────
export const fetchTimeOff = async (gpid: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${gpid}/time-off`);
  return res.data;
};

export const addTimeOff = async (
  gpid: string,
  request: { leaveType: string; startDate: string; endDate: string; note?: string }
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/employees/${gpid}/time-off`, request);
  return res.data;
};

export const deleteTimeOff = async (
  gpid: string,
  timeOffId: number | string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.delete(`${BASE}/employees/${gpid}/time-off/${timeOffId}`);
  return res.data;
};

// ─── Training & Compliance ─────────────────────────────────────────────────────
export const fetchTrainingRequirements = async (kind?: string): Promise<Record<string, any>[]> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/training-requirements`, { params: kind ? { kind } : {} });
  return res.data;
};

export const fetchEmployeeCompliance = async (gpid: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${gpid}/compliance`);
  return res.data;
};

export const updateComplianceRecord = async (
  gpid: string,
  requirementKey: string,
  request: { status: string; completedDate?: string }
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.patch(`${BASE}/employees/${gpid}/compliance/${requirementKey}`, request);
  return res.data;
};

export const fetchTeamCompliance = async (locationName: string): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/compliance`, { params: { locationName } });
  return res.data;
};

// ─── Home: New Hire Onboarding ramp & Location Workload ───────────────────────
export interface RampCheckpoint {
  phase: string;
  shortLabel: 'W1' | 'W2' | 'W3' | 'M1' | 'M2' | 'M3' | string;
  week?: number;
  state: 'done' | 'current' | 'ahead' | 'late' | string;
  done: number;
  total: number;
  tooltip?: string;
}

export interface RampHire {
  gpid: string;
  fullName: string;
  role?: string;
  hireDate?: string;
  dayOfOnboarding?: number;
  dayTotal?: number;
  subtitle?: string;
  lateCount?: number;
  status?: string;
  statusLabel?: string;
  checkpoints: RampCheckpoint[];
  profileTooltip?: string;
  narrowTooltip?: string;
}

export interface OnboardingRampResponse {
  locationName?: string;
  total: number;
  tag?: string;
  hires: RampHire[];
}

export const fetchOnboardingRamp = async (locationName: string): Promise<OnboardingRampResponse> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/onboarding/ramp`, { params: { locationName } });
  return res.data;
};

export type WorkloadTimeframeParam = 'CW' | 'PERIOD' | 'QUARTER' | 'ALL_OPEN';

export interface WorkloadCategory {
  type: 'PULSE_CHECKS' | 'OBSERVATIONS' | 'TRAINING' | 'CERTIFICATIONS' | 'MILESTONES' | string;
  label: string;
  completed: number;
  total: number;
  pct: number;
  open: number;
  overdue: number;
  upcoming: number;
  missed: number;
  detail?: string;
  progressText?: string;
}

export interface WorkloadLeaderSegment {
  key: string;
  count: number;
  pct: number;
}

export interface WorkloadLeader {
  gpid?: string | null;
  email?: string | null;
  name?: string | null;
  displayName?: string | null;
  shift?: string | null;
  completed: number;
  total: number;
  pct: number;
  open: number;
  overdue: number;
  upcoming: number;
  missed: number;
  workloadSharePct?: number;
  coverageDelta?: number;
  coverText?: string | null;
  detail?: string | null;
  scoreText?: string | null;
  completeText?: string | null;
  segments: WorkloadLeaderSegment[];
  currentUser?: boolean;
}

export interface LocationWorkloadResponse {
  summary: {
    totalObligations: number;
    completedObligations: number;
    openObligations: number;
    overdueObligations: number;
    missedObligations: number;
    completionPct: number;
    tag?: string;
    leadText?: string;
  };
  timeframeTooltips?: Record<string, string>;
  timeframeDescription?: string;
  startDate?: string;
  endDate?: string;
  categories: WorkloadCategory[];
  siteNote?: string;
  emptyText?: string;
  leaderLeadText?: string;
  leaderVisibleCount?: number;
  leaderNote?: string;
  leaders: WorkloadLeader[];
}

export const fetchLocationWorkload = async (
  locationName: string,
  timeframe: WorkloadTimeframeParam
): Promise<LocationWorkloadResponse> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/location-workload`, { params: { locationName, timeframe } });
  return res.data;
};

// ─── Task Queue ────────────────────────────────────────────────────────────────
export interface QueueParams {
  locationName?: string;
  locationId?: number;
  sapId?: string;
  scope?: 'MY_TASKS' | 'ALL_LOCATION_TASKS';
  /** Logged-in user's email — sent so MY_TASKS scopes the queue to this user. */
  userEmail?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  size?: number;
}

export const fetchQueue = async (params: QueueParams = {}): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/queue`, { params });
  return res.data;
};

export const refreshQueue = async (): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/queue/refresh`);
  return res.data;
};

// ─── Employee Profile ──────────────────────────────────────────────────────────
export const fetchEmployeeProfile = async (
  employeeId: string,
  clientTimestamp: string,
  locationName?: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${employeeId}`, {
    params: { clientTimestamp, ...(locationName ? { locationName } : {}) },
  });
  return res.data;
};

export const fetchMetricTrend = async (
  employeeId: string,
  metricName: 'dps' | 'ku' | 'gapTime' | 'casesPicked' | 'sia',
  clientTimestamp: string,
  splitBy: 'WEEK' | 'PERIOD' = 'WEEK'
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${employeeId}/metrics/trend/${metricName}`, {
    params: { clientTimestamp, splitBy },
  });
  return res.data;
};

export const fetchEmployeeOnboardingPlan = async (
  employeeId: string,
  locationName?: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${employeeId}/onboarding`, {
    params: locationName ? { locationName } : {},
  });
  return res.data;
};

export const saveEmployeeOnboardingPlan = async (
  employeeId: string,
  request: Record<string, any>,
  locationName?: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/employees/${employeeId}/onboarding/save`, request, {
    params: locationName ? { locationName } : {},
  });
  return res.data;
};

export interface OnboardingFormQuestionsParams {
  /** Probation form: '30' = Milestone 1, '60' = Milestone 2, '90' = Milestone 3. */
  probForm?: '30' | '60' | '90';
  locationType?: string | null;
  organizationType?: string | null;
  is3pl?: boolean | null;
}

export const fetchOnboardingFormQuestions = async ({
  probForm,
  locationType,
  organizationType,
  is3pl,
}: OnboardingFormQuestionsParams): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/onboarding/form-questions`, {
    params: {
      ...(probForm ? { Prob_Form: probForm } : {}),
      ...(locationType ? { locationType } : {}),
      ...(organizationType ? { organizationType } : {}),
      is3pl: is3pl ?? false,
      status: 'Active',
    },
  });
  return res.data;
};

export const completeOnboardingItem = async (
  gpid: string,
  itemKey: string,
  request: Record<string, any>
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/employees/${gpid}/onboarding/items/${itemKey}/complete`, request);
  return res.data;
};

export const fetchOnboardingItemRecord = async (
  gpid: string,
  itemKey: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/employees/${gpid}/onboarding/items/${itemKey}/record`);
  return res.data;
};

// ─── Activity Management (Pulse Check & Observation) ──────────────────────────
/** Assignee (logged-in user) and owner (their manager) of the Action Plan task a new
 *  activity may create; resolved client-side from the user/employee directories. */
export interface StartActivityAssigneeOwner {
  assigneeEmail?: string;
  assigneeGpid?: string;
  assigneeName?: string;
  assigneeRole?: string;
  assigneeRoleId?: string | number;
  ownerName?: string;
  ownerGpid?: string | number;
  ownerRole?: string;
  ownerRoleId?: string | number;
}

export interface StartActivityRequest extends StartActivityAssigneeOwner {
  activityType: 'OBSERVATION' | 'PULSE_CHECK';
  employeeId: string | number;
  locationId: string | number;
  clientTimestamp: string;
  clientTimezone: string;
  observationType?: 'Full' | 'Routine';
  locationName?: string;
  locationSapId?: string;
  /** Role the user selected for this employee in the Begin Activity overlay. */
  role?: string;
  /** Existing queue task to attach the new activity to (no new task is created). */
  taskId?: string | number;
}

export interface StartActivityResponse
  extends Partial<Omit<ResumeActivityResponse, 'activityId' | 'status' | 'stage'>> {
  activityId: number | string;
  taskId: number | string;
  status: string;
  isAdHoc: boolean;
  fiscalYear: number;
  fiscalPeriod: number;
  fiscalWeek: number;
  stage: string;
}

/** START returns the already-open activity (same shape as RESUME) instead of creating a new one
 *  when one exists for the employee + role + type. Only that response carries saved content. */
export const isExistingActivityResponse = (res: StartActivityResponse): boolean =>
  ['balance_feedback', 'feedback', 'signatures', 'checklistSelections', 'coachingResult'].some((k) => k in res);

export const startActivity = async (request: StartActivityRequest): Promise<StartActivityResponse> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/activities/start`, request);
  return res.data;
};

// Queue rows resume by taskId; Employee Profile > All History rows resume by activityId
// (they carry no taskId). Undefined fields are dropped from the JSON body.
export const resumeActivity = async (
  taskId: string | number | undefined,
  activityType: 'OBSERVATION' | 'PULSE_CHECK',
  activityId?: string | number
): Promise<ResumeActivityResponse> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/activities`, { action: 'RESUME', activityType, taskId, activityId });
  return res.data;
};

export interface ResumeActivityImage {
  /** Blob storage URL of the previously uploaded image (despite the field name). */
  imageBase64: string;
  imageId: string;
  description: string;
  checklistItemId: number | string;
}

export interface ResumeCustomChecklistItem {
  id: string;
  checklistItem: string;
}

export interface ResumeActivityResponse {
  activityId: number | string;
  status: string;
  /** Present for OBSERVATION resumes; absent for PULSE_CHECK. */
  stage?: string;
  activityType: 'OBSERVATION' | 'PULSE_CHECK';
  observationType: 'Full' | 'Routine' | null;
  /** JSON-encoded map of checklistItemId -> boolean, e.g. '{"3":true,"25":true}'. */
  checklistSelections?: string | Record<string, boolean>;
  /** JSON-encoded list or parsed list of custom checklist items. */
  customChecklist?: string | ResumeCustomChecklistItem[];
  checklistStages?: { label: string; stage: string }[];
  images?: ResumeActivityImage[];
  /** Observation & Pulse Check: { recognize, coach, sustain } (legacy: { well, improve }). */
  balance_feedback?: Record<string, string | null>;
  feedback?: Record<string, string | null>;
  /** Primary open coaching item's saved follow-up result/feedback. */
  coachingResult?: any;
  supervisorFeedback?: string | null;
  signatures?: {
    employeeSignature?: {
      blob?: string;
      imageUrl?: string;
      imageData?: string;
      signed?: boolean;
      timestamp?: string;
      signedOn?: string;
      signerName?: string;
      name?: string;
    };
    managerSignature?: {
      blob?: string;
      imageUrl?: string;
      imageData?: string;
      signed?: boolean;
      timestamp?: string;
      signedOn?: string;
      signerName?: string;
      name?: string;
    };
    managerSigned?: boolean;
    employeeSigned?: boolean;
  };
  messageKey?: string;
}

export const saveActivityDraft = async (
  activityId: string | number,
  request: Record<string, any>
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/activities/${activityId}/save`, request);
  return res.data;
};

export const completeActivity = async (
  activityId: string | number,
  request: Record<string, any>
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/activities/${activityId}/complete`, request);
  return res.data;
};

export const fetchActivityDetail = async (activityId: string | number): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/activities/${activityId}`);
  return res.data;
};

// Call when the user exits Begin Activity / the activity screen without saving or completing,
// so the NEW row created by startActivity/resumeActivity doesn't linger forever.
export const discardActivity = async (activityId: string | number): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.delete(`${BASE}/activities/${activityId}`);
  return res.data;
};

export const uploadActivityImage = async (
  activityId: string | number,
  stage: string,
  options: { checklistItemId?: string; description?: string } = {}
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/activities/${activityId}/images`, null, {
    params: { stage, ...options },
  });
  return res.data;
};

// ─── Activity History ──────────────────────────────────────────────────────────
/** Serializes array params as repeated keys (?roles=A&roles=B), not roles[]=A. */
const serializeActivitiesParams = (p: Record<string, any>): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(p)) {
    if (Array.isArray(value)) {
      value.forEach((v: any) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
    } else if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
};

export interface ListActivitiesParams {
  locationName?: string;
  locationId?: number;
  sapId?: string;
  search?: string;
  employee?: string;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  size?: number;
  // Multi-select column filters (All History table filter dropdowns) — sent as repeated keys.
  roles?: string[];
  types?: string[];
  results?: string[];
  statuses?: string[];
  createdByEmails?: string[];
  completedByEmails?: string[];
}

export const fetchActivities = async (params: ListActivitiesParams = {}): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/activities`, { params, paramsSerializer: serializeActivitiesParams });
  return res.data;
};

// ─── Task Reassignment ─────────────────────────────────────────────────────────
export const reassignTask = async (
  taskId: string | number,
  request: { actingUserGpid: string; reassignToGpid: string; reason: string }
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/cadence/tasks/${taskId}/reassign`, request);
  return res.data;
};

export const setTaskCoverage = async (request: {
  locationName: string;
  originalLeaderGpid: string;
  coveredByGpid: string;
  coverFrom: string;
  coverTo: string;
}): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.post(`${BASE}/cadence/coverage`, request);
  return res.data;
};

// ─── Checklist Items ────────────────────────────────────────────────────────────
export interface ChecklistItemsParams {
  category?: string;
  observation_type?: string;
  role?: string;
  locationType?: string;
  organizationType?: string;
  is3pl?: string;
  status?: number;
}

export interface ChecklistItem {
  itemId: string;
  /** Prompt text; may be null in some backend rows — fall back to `observation`. */
  question: string | null;
  observation: string;
  category: string;
  currentCategory: string | null;
  sparcCategory: string | null;
  material: string | null;
  palletType: string | null;
  role: string;
  observation_type: string;
  locationType: string;
  organizationType: string;
  is3pl: string;
  status: number;
  isActive: boolean;
}

export const fetchChecklistItems = async (
  params: ChecklistItemsParams = {}
): Promise<ChecklistItem[]> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/checklist-items`, { params });
  return res.data;
};

// ─── People Hub Admin Targets & Glidepath ──────────────────────────────────────
export interface AdminTargetWeekItem {
  Id?: number;
  DPS_Target?: number | null;
  CPH_Target?: number | null;
  Gap_Time_Target?: number | null;
  KU_Target?: number | null;
  BPA_Target?: number | null;
  Week?: string;
}

export interface AdminTargetsGlidepath {
  Milestone1: number;
  Milestone2: number;
  Milestone3: number;
  PreferredMethod: number;
  TrainingWeek: number;
  CertificationWeek: number;
}

export interface GetAdminTargetsResponse {
  weeks: AdminTargetWeekItem[];
  adminTargets: AdminTargetsGlidepath;
}

export interface PutAdminTargetsPayload {
  Location_Name: string;
  updates: AdminTargetWeekItem[];
  adminTargets: AdminTargetsGlidepath;
}

export const fetchAdminPeopleTargets = async (
  locationName: string
): Promise<GetAdminTargetsResponse> => {
  const api = createApiClient();
  const res = await api.get(`${BASE}/admin/targets`, {
    params: { Location_Name: locationName },
  });
  return res.data;
};

export const updateAdminPeopleTargets = async (
  payload: PutAdminTargetsPayload
): Promise<any> => {
  const api = createApiClient();
  const res = await api.put(`${BASE}/admin/targets`, payload);
  return res.data;
};


// ─── Coaching History ───────────────────────────────────────────────────────────
/** Serializes array params as repeated keys (?employeeNames=A&employeeNames=B), not employeeNames[]=A. */
const serializeCoachingHistoryParams = (p: Record<string, any>): string => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(p)) {
    if (Array.isArray(value)) {
      value.forEach((v: any) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
    } else if (value !== undefined && value !== null && value !== '') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
};

export interface CoachingHistoryParams {
  locationName: string;
  employeeNames?: string[];
  actionTypes?: string[];
  focusAreas?: string[];
  managers?: string[];
  sources?: string[];
  statuses?: string[];
  results?: string[];
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  size?: 25 | 50 | 100;
}

export interface CoachingHistoryRecord {
  id: string;
  employeeId: string | null;
  employeeName: string;
  employeeRole?: string | null;
  actionType: string;
  focusArea: string;
  actionPlan: string;
  opportunity?: string | null;
  support?: string | null;
  managerSubmitted: string;
  activityDate: string;
  source: string;
  status: string;
  result: string | null;
  actionPlanId?: string | null;
}

export interface CoachingHistoryFilterOptions {
  employeeNames: string[];
  actionTypes: string[];
  focusAreas: string[];
  managers: string[];
  sources: string[];
  statuses: string[];
  results: string[];
}

export interface CoachingHistoryResponse {
  records: CoachingHistoryRecord[];
  total: number;
  page: number;
  size: number;
  filterOptions: CoachingHistoryFilterOptions;
}

const EMPTY_FILTER_OPTIONS: CoachingHistoryFilterOptions = {
  employeeNames: [], actionTypes: [], focusAreas: [], managers: [], sources: [], statuses: [], results: [],
};

/** Tolerates a few plausible backend response shapes (records/items/data, total/totalCount/count, filterOptions/facets/filters). */
const normalizeCoachingHistoryResponse = (raw: Record<string, any>): CoachingHistoryResponse => {
  const rows: any[] = raw?.records ?? raw?.items ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
  const total: number = raw?.total ?? raw?.totalCount ?? raw?.count ?? rows.length;
  const page: number = raw?.page ?? 1;
  const size: number = raw?.size ?? rows.length ?? 25;
  const rawFilters = raw?.filterOptions ?? raw?.facets ?? raw?.filters ?? {};

  const records: CoachingHistoryRecord[] = rows.map((r: any) => ({
    id: String(r.id ?? r.actionPlanId ?? r.coachingItemId ?? `${r.employeeId ?? ''}-${r.activityDate ?? ''}-${r.focus ?? r.focusArea ?? ''}`),
    employeeId: r.employeeId != null ? String(r.employeeId) : (r.employeeGpid != null ? String(r.employeeGpid) : (r.gpid != null ? String(r.gpid) : null)),
    employeeName: r.employeeName ?? r.associate ?? r.name ?? 'Unknown employee',
    employeeRole: r.employeeRole ?? r.role ?? null,
    actionType: r.actionType ?? r.category ?? r.type ?? '',
    focusArea: r.focusArea ?? r.focus ?? '',
    actionPlan: r.actionPlan ?? r.plan ?? '',
    opportunity: r.opportunity ?? r.summary ?? null,
    support: r.support ?? r.supportNeeded ?? r.employeeSupport ?? null,
    managerSubmitted: r.managerSubmitted ?? r.manager ?? r.submittedBy ?? r.createdByEmail ?? r.created_by_email ?? '',
    activityDate: r.activityDate ?? r.date ?? '',
    source: r.source ?? '',
    status: r.status ?? '',
    result: r.result ?? null,
    actionPlanId: r.actionPlanId != null ? String(r.actionPlanId) : (r.id != null ? String(r.id) : null),
  }));

  return {
    records,
    total,
    page,
    size,
    filterOptions: {
      employeeNames: rawFilters.employeeNames ?? rawFilters.employeeName ?? EMPTY_FILTER_OPTIONS.employeeNames,
      actionTypes: rawFilters.actionTypes ?? rawFilters.actionType ?? EMPTY_FILTER_OPTIONS.actionTypes,
      focusAreas: rawFilters.focusAreas ?? rawFilters.focusArea ?? EMPTY_FILTER_OPTIONS.focusAreas,
      managers: rawFilters.managers ?? rawFilters.managerSubmitted ?? EMPTY_FILTER_OPTIONS.managers,
      sources: rawFilters.sources ?? rawFilters.source ?? EMPTY_FILTER_OPTIONS.sources,
      statuses: rawFilters.statuses ?? rawFilters.status ?? EMPTY_FILTER_OPTIONS.statuses,
      results: rawFilters.results ?? rawFilters.result ?? EMPTY_FILTER_OPTIONS.results,
    },
  };
};

export const fetchCoachingHistory = async (
  params: CoachingHistoryParams
): Promise<CoachingHistoryResponse> => {
  const {
    locationName, employeeNames, actionTypes, focusAreas, managers, sources, statuses, results,
    search, sortBy = 'activityDate', sortOrder = 'DESC', page = 1, size = 25,
  } = params;

  const queryParams: Record<string, any> = { locationName, sortBy, sortOrder, page, size };
  if (employeeNames?.length) queryParams.employeeNames = employeeNames;
  if (actionTypes?.length) queryParams.actionTypes = actionTypes;
  if (focusAreas?.length) queryParams.focusAreas = focusAreas;
  if (managers?.length) queryParams.managers = managers;
  if (sources?.length) queryParams.sources = sources;
  if (statuses?.length) queryParams.statuses = statuses;
  if (results?.length) queryParams.results = results;
  if (search?.trim()) queryParams.search = search.trim();

  const api = createApiClient();
  const res = await api.get(`${BASE}/coaching-history`, {
    params: queryParams,
    paramsSerializer: serializeCoachingHistoryParams,
  });
  return normalizeCoachingHistoryResponse(res.data);
};

export const closeCoachingItem = async (
  id: string | number,
  supervisorFeedback?: string
): Promise<Record<string, any>> => {
  const api = createApiClient();
  const res = await api.patch(`${BASE}/coaching-items/${id}`, { supervisorFeedback });
  return res.data;
};

export const exportCoachingHistoryCsv = async (
  params: Omit<CoachingHistoryParams, 'page' | 'size'>
): Promise<Blob> => {
  const { locationName, employeeNames, actionTypes, focusAreas, managers, sources, statuses, results, search, sortBy, sortOrder } = params;
  const queryParams: Record<string, any> = { locationName, sortBy, sortOrder };
  if (employeeNames?.length) queryParams.employeeNames = employeeNames;
  if (actionTypes?.length) queryParams.actionTypes = actionTypes;
  if (focusAreas?.length) queryParams.focusAreas = focusAreas;
  if (managers?.length) queryParams.managers = managers;
  if (sources?.length) queryParams.sources = sources;
  if (statuses?.length) queryParams.statuses = statuses;
  if (results?.length) queryParams.results = results;
  if (search?.trim()) queryParams.search = search.trim();

  const api = createApiClient();
  const res = await api.get(`${BASE}/coaching-history/export`, {
    params: queryParams,
    paramsSerializer: serializeCoachingHistoryParams,
    responseType: 'blob',
  });
  return res.data;
};

